import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { profileApi } from '@/entities/Profile';
import { authApi } from '../../api/authApi';
import { clearAuthAndRedirect } from '../../lib/utils/authUtils';
import { AuthSchema } from '../types/AuthSchema';

const isBrowser = typeof window !== 'undefined';

const readIsAuthFromStorage = (): boolean => {
  if (!isBrowser) return false;
  if (sessionStorage.getItem('isAuth') === 'true') return true;
  if (localStorage.getItem('isAuth') === 'true') {
    sessionStorage.setItem('isAuth', 'true');
    return true;
  }
  return false;
};

const writeIsAuthToStorage = (value: boolean): void => {
  if (!isBrowser) return;
  const str = value.toString();
  sessionStorage.setItem('isAuth', str);
  localStorage.setItem('isAuth', str);
};

const initialState: AuthSchema = {
  isAuth: readIsAuthFromStorage(),
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setIsAuth: (state: Draft<AuthSchema>, action: PayloadAction<boolean>) => {
      state.isAuth = action.payload;
      writeIsAuthToStorage(action.payload);
    },
    logout: (state: Draft<AuthSchema>) => {
      // Только меняем Redux state, очистка localStorage и кеша RTK Query
      // происходит в resetAuthState() для избежания дублирования
      state.isAuth = false;
    },
  },
  extraReducers: (builder) => {
    // После успешного signIn устанавливаем isAuth
    builder.addMatcher(authApi.endpoints.signIn.matchFulfilled, (state: Draft<AuthSchema>) => {
      state.isAuth = true;
    });

    // После успешного resetPassword устанавливаем isAuth
    builder.addMatcher(
      authApi.endpoints.resetPassword.matchFulfilled,
      (state: Draft<AuthSchema>) => {
        state.isAuth = true;
      },
    );

    builder.addMatcher(
      authApi.endpoints.setPassword.matchFulfilled,
      (state: Draft<AuthSchema>) => {
        state.isAuth = true;
      },
    );

    // Активация сразу открывает сессию
    builder.addMatcher(
      authApi.endpoints.activateAccount.matchFulfilled,
      (state: Draft<AuthSchema>) => {
        state.isAuth = true;
      },
    );

    // После успешной загрузки профиля - пользователь авторизован
    builder.addMatcher(
      profileApi.endpoints.getMyProfile.matchFulfilled,
      (state: Draft<AuthSchema>) => {
        state.isAuth = true;
        writeIsAuthToStorage(true);
      },
    );

    // Если профиль не загрузился - разлогиниваем
    builder.addMatcher(
      profileApi.endpoints.getMyProfile.matchRejected,
      (state: Draft<AuthSchema>) => {
        state.isAuth = false;
        if (isBrowser) {
          clearAuthAndRedirect('/login');
        }
      },
    );
  },
});

export const { actions: authActions } = authSlice;
export const { reducer: authReducer } = authSlice;
